import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Search, 
  Users, 
  Edit, 
  Trash2,
  Phone,
  Mail,
  Star,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const ContactsPage = () => {
  const { isBarbiePink } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Check for org_id and action from URL params
  const preselectedOrgId = searchParams.get('org_id');
  const actionParam = searchParams.get('action');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const [formData, setFormData] = useState({
    org_id: '',
    name: '',
    role: '',
    email: '',
    phone: '',
    is_primary: false,
    notes: ''
  });

  const fetchOrganizations = async () => {
    try {
      const response = await authFetch(`${API}/organizations?limit=1000`);
      if (response.ok) {
        const result = await response.json();
        setOrganizations(result.data || result);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orgFilter && orgFilter !== 'all') params.append('org_id', orgFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('page', currentPage);
      params.append('limit', pageSize);

      const response = await authFetch(`${API}/contacts?${params}`);
      if (response.ok) {
        const result = await response.json();
        setContacts(result.data || []);
        setTotalContacts(result.total || 0);
        setTotalPages(result.total_pages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [orgFilter, debouncedSearch, currentPage, pageSize]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Handle preselected org from URL params
  useEffect(() => {
    if (preselectedOrgId && organizations.length > 0) {
      setOrgFilter(preselectedOrgId);
      
      // If action is 'add', open the dialog with org preselected
      if (actionParam === 'add') {
        setEditingContact(null);
        setFormData({
          org_id: preselectedOrgId,
          name: '',
          role: '',
          email: '',
          phone: '',
          is_primary: false,
          notes: ''
        });
        setDialogOpen(true);
      }
    }
  }, [preselectedOrgId, actionParam, organizations]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [orgFilter, pageSize]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingContact 
        ? `${API}/contacts/${editingContact.contact_id}`
        : `${API}/contacts`;
      
      const response = await authJsonFetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingContact ? 'Contact updated' : 'Contact created');
        setDialogOpen(false);
        resetForm();
        fetchContacts();
      } else {
        toast.error('Failed to save contact');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const response = await authFetch(`${API}/contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Contact deleted');
        fetchContacts();
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      org_id: contact.org_id,
      name: contact.name,
      role: contact.role,
      email: contact.email || '',
      phone: contact.phone || '',
      is_primary: contact.is_primary,
      notes: contact.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      org_id: '',
      name: '',
      role: '',
      email: '',
      phone: '',
      is_primary: false,
      notes: ''
    });
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.org_id === orgId);
    return org?.name || 'Unknown';
  };

  return (
    <div data-testid="contacts-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contacts</h1>
          <p className="text-muted-foreground mt-1">
            Manage contacts for your organizations ({totalContacts} total)
          </p>
        </div>
        <Button 
          data-testid="add-contact-btn"
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </Button>
      </div>

      {/* Filters */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="contact-search-input"
                placeholder="Search by name, email, role or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger data-testid="org-filter" className="w-full sm:w-64">
                <SelectValue placeholder="All Organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="contact-page-size" className="w-full sm:w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className={cn(isBarbiePink && "card-brutalist")}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead className="hidden md:table-cell">Role</TableHead>
                <TableHead className="hidden lg:table-cell">Contact</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.contact_id} data-testid={`contact-row-${contact.contact_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          isBarbiePink ? "bg-pink-500/20" : "bg-blue-500/10"
                        )}>
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{contact.name}</span>
                          {contact.is_primary && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        data-testid={`contact-org-link-${contact.contact_id}`}
                        className="cursor-pointer hover:bg-accent transition-colors"
                        onClick={() => navigate(`/organizations?org=${contact.org_id}`)}
                        title="Open organization"
                      >
                        {contact.org_name || getOrgName(contact.org_id)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {contact.role}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-1 text-sm">
                        {contact.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                          </div>
                        )}
                        {contact.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            {contact.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`edit-contact-${contact.contact_id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(contact)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-contact-${contact.contact_id}`}
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(contact.contact_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination Controls */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalContacts)} of {totalContacts} contacts
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-testid="contact-first-page-btn"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="contact-prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="contact-next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-testid="contact-last-page-btn"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="org_id">Organization *</Label>
                <Select 
                  value={formData.org_id} 
                  onValueChange={(v) => setFormData({...formData, org_id: v})}
                >
                  <SelectTrigger data-testid="contact-org-select">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Role *</Label>
                <Input
                  id="role"
                  data-testid="contact-role-input"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  placeholder="e.g., Principal, Coach, Admin"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_primary"
                  data-testid="contact-primary-checkbox"
                  checked={formData.is_primary}
                  onCheckedChange={(checked) => setFormData({...formData, is_primary: checked})}
                />
                <Label htmlFor="is_primary" className="cursor-pointer">
                  Primary contact for organization
                </Label>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="contact-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" data-testid="save-contact-btn">
                {editingContact ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
